import * as React from 'react'
import {
  PrimaryButton,
  OutlineButton as OButton,
  ButtonLink as BLink,
} from '@chegg/fanta'
import styled, { css } from 'styled-components'
import { font, colors } from '@chegg-ui/theme'
import { AspiraStack } from '@chegg-wt/core/theme/fanta/fonts'

type FantaButtonSize = 'large' | 'medium' | 'small' | 'mini'

export const Button: React.FunctionComponent<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    isLoading?: boolean
    isHoverAnimated?: boolean
    size?: FantaButtonSize
  }
> = ({
  className,
  children,
  isLoading,
  isHoverAnimated = true,
  size = 'large',
  ...rest
}) => (
  <PrimaryButton
    isLoading={isLoading}
    className={className}
    isHoverAnimated={isHoverAnimated}
    size={size}
    {...rest}
  >
    {children}
  </PrimaryButton>
)

export const OutlineButton: React.FunctionComponent<
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    as: keyof JSX.IntrinsicElements | React.ComponentType<any>
    size?: FantaButtonSize
  }
> = ({ className, children, as, size = 'large', ...rest }) => (
  <OButton className={className} as={as} isHoverAnimated size={size} {...rest}>
    {children}
  </OButton>
)

export const ButtonLink: React.FunctionComponent<
  React.AnchorHTMLAttributes<HTMLAnchorElement> & {
    size?: FantaButtonSize
  } & React.RefAttributes<HTMLAnchorElement>
> = React.forwardRef(({ className, children, size = 'large', ...rest }, ref) => (
  <BLink ref={ref} className={className} isHoverAnimated size={size} {...rest}>
    {children}
  </BLink>
))

export const SpecialOutline = css`
  &&& {
    ${AspiraStack};
    border: 2px solid ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};
    color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    &&&:hover {
      background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
      border: 2px solid ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
      color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};
    }
    &&&:hover * {
      color: ${({ theme }) => theme.Colors.forInterface.interfaceColor11};
    }
  }
`
// todo move this to a utility
export const Special = css`
  &&& {
    ${AspiraStack};
    border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor01};
    color: ${colors.white};
    &&&:hover {
      border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
      background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor23};
      color: ${colors.white};
    }
    &&&: disabled {
      background-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor14};
      border-color: ${({ theme }) => theme.Colors.forInterface.interfaceColor14};
    }
  }
`

export const Large = css`
  box-shadow: none;
  height: 44px;
  line-height: 44px;
  border-radius: 2rem;
  padding: 0 20px;
  &&& {
    font-size: 20px;
    font-weight: ${font.weight.bold};
  }
`

export const Small = css`
  height: 34px;
  line-height: 34px;
  border-radius: 2rem;
  padding: 0 20px;
  &&& {
    font-size: ${font.size.px16};
    font-weight: ${font.weight.bold};
  }
`

export const Mini = css`
  border-radius: 2rem;
  padding: 0 10px;
  &&& {
    font-size: ${font.size.px14};
    font-weight: ${font.weight.bold};
  }
`

const FantaButtonSizes = {
  mini: Mini,
  small: Small,
  large: Large,
}

export const SpecialOutlineButton = styled(Button)`
  ${SpecialOutline};
  ${({ size = 'large' }) => FantaButtonSizes[size]};
`

export const SpecialButton = styled(Button)`
  ${Special};
  ${({ size = 'large' }) => FantaButtonSizes[size]};
`

export const SpecialButtonLink = styled(ButtonLink)`
  ${Special};
  text-decoration: none;
  ${({ size = 'large' }) => FantaButtonSizes[size]};
`

export const SpecialOutlineButtonLink = styled(ButtonLink)`
  ${SpecialOutline};
  text-decoration: none;
  ${({ size = 'large' }) => FantaButtonSizes[size]};
`
